import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';

const Menu = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef();
  const logoRef = useRef();
  const linksRef = useRef([]);
  const buttonRef = useRef();
  const hamburgerRef = useRef();
  const circlesRef = useRef([]);

  useEffect(() => {
    // Animation d'entrée du menu
    const tl = gsap.timeline();
    
    // Logo slide from left
    tl.fromTo(logoRef.current,
      { x: -50, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
    );

    // Links stagger from right
    tl.fromTo(linksRef.current,
      { x: 30, opacity: 0 },
      { 
        x: 0, 
        opacity: 1, 
        duration: 0.6, 
        stagger: 0.1,
        ease: "power2.out" 
      },
      "-=0.4"
    );

    // Button with special effect
    tl.fromTo(buttonRef.current,
      { scale: 0, opacity: 0 },
      { 
        scale: 1, 
        opacity: 1, 
        duration: 0.8, 
        ease: "back.out(1.7)" 
      },
      "-=0.2"
    );

    // Animate circles on button hover
    const circles = circlesRef.current;
    circles.forEach((circle, index) => {
      gsap.set(circle, {
        x: circle.style.getPropertyValue('--x'),
        y: circle.style.getPropertyValue('--y')
      });
    });

  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    
    if (!isMenuOpen) {
      gsap.to(hamburgerRef.current.children[0], {
        rotation: 45,
        y: 3,
        duration: 0.3,
        ease: "power2.out"
      });
      gsap.to(hamburgerRef.current.children[1], {
        rotation: -45,
        y: -3,
        duration: 0.3,
        ease: "power2.out"
      });
    } else {
      gsap.to(hamburgerRef.current.children[0], {
        rotation: 0,
        y: 0,
        duration: 0.3,
        ease: "power2.out"
      });
      gsap.to(hamburgerRef.current.children[1], {
        rotation: 0,
        y: 0,
        duration: 0.3,
        ease: "power2.out"
      });
    }
  };

  const addToLinksRef = (el) => {
    if (el && !linksRef.current.includes(el)) {
      linksRef.current.push(el);
    }
  };

  const addToParticlesRef = (el) => {
    if (el && !circlesRef.current.includes(el)) {
      circlesRef.current.push(el);
    }
  };

  const navItems = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Work", href: "#experience" }
  ];

  // Configuration des particules animées - Gold et Violet
  const animatedParticles = [
    { id: "gold-top", color: "rgba(202, 138, 4, 0.8)", blur: "8px", x: "0", y: "-40px" },
    { id: "gold-right", color: "rgba(234, 179, 8, 0.7)", blur: "12px", x: "92px", y: "8px" },
    { id: "purple-left", color: "rgba(147, 51, 234, 0.7)", blur: "14px", x: "-12px", y: "-12px" },
    { id: "purple-right", color: "rgba(124, 58, 237, 0.7)", blur: "14px", x: "80px", y: "-12px" },
    { id: "violet-center", color: "rgba(139, 92, 246, 0.6)", blur: "16px", x: "12px", y: "-4px" },
    { id: "gold-bottom", color: "rgba(217, 119, 6, 0.8)", blur: "16px", x: "56px", y: "16px" }
  ];

  return (
    <nav 
      ref={menuRef}
      className="fixed top-0 left-0 right-0 z-50 py-4"
    >
      <div className="px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* Logo à gauche - Transparent */}
          <a 
            ref={logoRef}
            href="/" 
            className="flex items-center z-10"
          >
            <img 
              src="/SAFAA.png"
              alt="Safaa" 
              className="h-32 w-auto"
            />
          </a>

          {/* Conteneur droit avec fond transparent foncé */}
          <div className="hidden lg:flex items-center space-x-6 bg-black/50 backdrop-blur-lg rounded-2xl px-6 py-3 border border-white/10">
            
            {/* Navigation Links */}
            <div className="flex items-center space-x-1">
              {navItems.map((item, index) => (
                <a
                  key={item.name}
                  ref={addToLinksRef}
                  href={item.href}
                  className="group relative px-4 py-2 text-white/80 hover:text-white transition-all duration-300"
                >
                  {/* Double text effect */}
                  <div className="relative overflow-hidden">
                    <div className="transition-transform duration-300 group-hover:-translate-y-full">
                      {item.name}
                    </div>
                    <div className="absolute inset-0 translate-y-full transition-transform duration-300 group-hover:translate-y-0 font-medium">
                      {item.name}
                    </div>
                  </div>
                  
                  {/* Hover underline */}
                  <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></div>
                </a>
              ))}
            </div>

            {/* Séparateur */}
            <div className="w-px h-6 bg-white/20"></div>

            {/* Bouton Contact Animé */}
            <a
              ref={buttonRef}
              href="#footer"
              className="relative animated-contact-button group"
            >
              <div className="button-container relative overflow-hidden rounded-2xl min-w-[120px] py-3 px-6">
                {/* Particules flottantes */}
                {animatedParticles.map((particle, index) => (
                  <div
                    key={particle.id}
                    ref={addToParticlesRef}
                    className={`floating-particle absolute w-10 h-10 rounded-full particle-${index + 1}`}
                    style={{
                      '--x': particle.x,
                      '--y': particle.y,
                      '--blur': particle.blur,
                      '--background': particle.color,
                      '--duration': '7s',
                      '--easing': 'linear'
                    }}
                  />
                ))}
                
                {/* Texte du bouton */}
                <span className="relative z-20 font-bold text-white text-sm tracking-wide drop-shadow-lg" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>
                  Contact
                </span>
              </div>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <div className="lg:hidden z-10">
            <button
              ref={hamburgerRef}
              onClick={toggleMenu}
              className="flex flex-col items-center justify-center w-8 h-8 space-y-1 bg-black/30 backdrop-blur-lg rounded-lg p-1.5"
            >
              <div className="w-5 h-0.5 bg-white transition-all duration-300 origin-center"></div>
              <div className="w-5 h-0.5 bg-white transition-all duration-300 origin-center"></div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-20 left-6 right-6 bg-black/80 backdrop-blur-lg rounded-2xl border border-white/10">
            <div className="px-6 py-4 space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block py-3 text-white/80 hover:text-white font-medium transition-colors duration-300 border-b border-white/10 last:border-b-0 text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              <div className="pt-2">
                <a
                  href="/contact"
                  className="block mobile-contact-button text-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="button-container relative overflow-hidden rounded-2xl py-3 px-6 min-w-[120px] mx-auto">
                    {/* Particules version mobile - seulement jaune et orange */}
                    {animatedParticles.slice(0, 2).map((particle, index) => (
                      <div
                        key={particle.id}
                        className={`floating-particle absolute w-8 h-8 rounded-full particle-${index + 1}`}
                        style={{
                          '--x': particle.x,
                          '--y': particle.y,
                          '--blur': particle.blur,
                          '--background': particle.color,
                          '--duration': '7s',
                          '--easing': 'linear'
                        }}
                      />
                    ))}
                    <span className="relative z-20 font-bold text-white text-sm drop-shadow-lg" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                      Contact
                    </span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .animated-contact-button {
          cursor: pointer;
          position: relative;
          border: none;
          border-radius: 24px;
          padding: 0;
          margin: 0;
          text-align: center;
          background: radial-gradient(circle, #ffd215, #fff172 80%);
          box-shadow: 0 0 14px rgba(255, 223, 87, 0.5);
          transition: all 0.3s ease;
        }

        .animated-contact-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 0 20px rgba(255, 223, 87, 0.7);
        }

        .button-container {
          -webkit-mask-image: -webkit-radial-gradient(white, black);
          border-radius: 24px;
          position: relative;
        }

        .button-container:before {
          content: "";
          pointer-events: none;
          position: absolute;
          z-index: 3;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          border-radius: 24px;
          box-shadow:
            inset 0 3px 12px rgba(255, 223, 52, 0.9),
            inset 0 -3px 4px rgba(255, 250, 215, 0.8);
        }

        .animated-contact-button:hover .floating-particle {
          animation-duration: 1400ms;
        }

        .floating-particle {
          position: absolute;
          border-radius: 50%;
          filter: blur(var(--blur, 8px));
          background: var(--background, transparent);
          transform: translate(var(--x, 0), var(--y, 0)) translateZ(0);
          animation: var(--animation, none) var(--duration, 7s) var(--easing, linear) infinite;
        }

        /* Animations simplifiées des particules */
        .particle-1 { animation: floatYellow 7s linear infinite; }
        .particle-2 { animation: floatOrange 7s linear infinite; }
        .particle-3 { animation: floatBlueLeft 7s linear infinite; }
        .particle-4 { animation: floatBlueRight 7s linear infinite; }
        .particle-5 { animation: floatPinkCenter 7s linear infinite; }
        .particle-6 { animation: floatPinkBottom 7s linear infinite; }

        @keyframes floatYellow {
          33% { transform: translate(0px, 16px) translateZ(0); }
          66% { transform: translate(12px, 64px) translateZ(0); }
        }
        @keyframes floatOrange {
          33% { transform: translate(80px, -10px) translateZ(0); }
          66% { transform: translate(72px, -48px) translateZ(0); }
        }
        @keyframes floatBlueLeft {
          33% { transform: translate(20px, 12px) translateZ(0); }
          66% { transform: translate(12px, 4px) translateZ(0); }
        }
        @keyframes floatBlueRight {
          33% { transform: translate(76px, -12px) translateZ(0); }
          66% { transform: translate(112px, -8px) translateZ(0); }
        }
        @keyframes floatPinkCenter {
          33% { transform: translate(84px, 28px) translateZ(0); }
          66% { transform: translate(40px, -32px) translateZ(0); }
        }
        @keyframes floatPinkBottom {
          33% { transform: translate(28px, -16px) translateZ(0); }
          66% { transform: translate(76px, -56px) translateZ(0); }
        }

        /* Version mobile */
        .mobile-contact-button .button-container {
          background: radial-gradient(circle, #ffd215, #fff172 80%);
          box-shadow: 0 0 14px rgba(255, 223, 87, 0.5);
          border-radius: 20px;
        }
      `}</style>
    </nav>
  );
};

export default Menu;